import axios from 'axios';
import { setUserId } from '@/plugins/analytics';
import { oktaAuthConfig, erorrMessages, additionalSecurityForMember, getDefaultAuthData } from '../store.constant';
import { storeMutation } from '../storeMutations.constant';
import { storeGetters } from '../storeGetters.constant';
import { storeAction } from '../storeActions.constant';
import { getBasicHeaders, removeSpaces, getSpeakerBasicHeaders } from '../../common/utils/utils';
import store from '../store';

import { OktaAuth } from '@okta/okta-auth-js';
import { loginConstants } from '../../components/LoginPage/login.constants';
import { SpeakerApplicants } from '../../components/Speakers/speakerConstant';
import { appConstant } from '../../app.constant';
import { roles, routerNames } from "../../components/appCommon.constants";
import { addDays } from "@/common/utils/dateTime";
import router from "@/routes";

const authState = () => ({
    userAuthenticated: sessionStorage.getItem('loggedUserYpo') || null,
    currentUserId: null,
    baseURL: process.env.VUE_APP_BASE_URL,
    speakerBaseURL: process.env.VUE_APP_SPEAKER_BASE_URL,
    oktaRootUrl: process.env.VUE_APP_OKTA_URL,
    passwordRecoveryOptions: null,
    passwordResetToken: null,
    selectedRecoveryCodeType: null,
    oktaSession: null,
    sessionExpirationTime: null,
    accessTokenExpirationTime: null,
    authClient: new OktaAuth(oktaAuthConfig),
    loginErrorMessage: null,
    fromLogoutSession: false,
    userRecoveryOptions: null,
    passwordFurtherAssistance: null,
    selectTypePasswordDelivery: null,
    additionalSecurityObjectForPassword: null,
    postLoginRedirectRoute: null,
    permissionsData: null,
    userCommunities: null,
    isMemberProspect: false,
    isPendingResignation: false,
    isExceptionOccurredOnGetPermissions: false,
    hasConnectAccess: true,
    isSpeakerView: false,
    isExternalSpeaker: null,
    isChapterManager: false,
    cookiesPolicyAccepted: true,
    authData: getDefaultAuthData(),
    authFeatureFlag: null,
    isImpersonationMode: false,
    memberAdminControls: {
        canImpersonate: false,
        canAllowImpersonation: false,
    },
    resettingAuthToken: false,
    tokenRefreshTime: process.env.VUE_APP_YPO_OKTA_TOKEN_REFRESH_TIME || 3.6e+6, // 2 min for lower and 1 hour for prod
});

const authMutations = {
    [storeMutation.setAuthData]: (state, data) => {
        state.authData = data || null;
    },
    [storeMutation.resetAuthTokenStatus]: (state, data) => {
        state.resettingAuthToken = data;
    },
    [storeMutation.setAuthFeatureFlag]: (state, data) => {
        state.authFeatureFlag = data || null;
    },
    [storeMutation.setExternalSpeaker]: (state, data) => {
        state.isExternalSpeaker = data;
    },
    [storeMutation.setPostLoginRedirectRoute]: (state, data) => {
        state.postLoginRedirectRoute = data;
    },
    [storeMutation.setLoggedUser]: (state, data) => {
        state.userAuthenticated = data;
    },
    [storeMutation.setCurrentUserId]: (state, data) => {
        const impersonationKey = sessionStorage.getItem(appConstant.storageKeys.impersonationKey);
        state.isImpersonationMode = !!impersonationKey;
        state.currentUserId = impersonationKey || data;
    },
    [storeMutation.checkCookiePolicyAcceptance]: (state) => {
        state.cookiesPolicyAccepted = !!localStorage.getItem(`${state.currentUserId || 'general'}_${appConstant.storageKeys.ypoCookiesPolicyAccepted}`) || false;
    },
    [storeMutation.setCookiePolicy]: (state) => {
        localStorage.setItem(`${state.currentUserId || 'general'}_${appConstant.storageKeys.ypoCookiesPolicyAccepted}`, 'true');
        state.cookiesPolicyAccepted = true;
    },
    [storeMutation.removeLoggedUser]: (state) => {
        state.userAuthenticated = null;
    },
    [storeMutation.removeCurrentUserId]: (state) => {
        state.currentUserId = null;
    },
    [storeMutation.resetState]: () => {
        const { matrixClient } = store.state.messenger;
        try {
            if (matrixClient) {
                matrixClient.logout().then(() => {
                    matrixClient.clearStores();
                    matrixClient.stopClient();
                });
            }
        } catch (e) {
            console.error('Could not clear matrix state', e);
        }

        //resetting all state modules
        let newState = {};
        Object.keys(store.state).forEach((key) => {
            if (store.state[key]) {
                newState = {
                    ...newState,
                    [key]: store.state[key].initialState ? store.state[key].initialState : store.state[key],
                };
            }
        });
        store.replaceState(Object.assign({}, newState));
    },
    [storeMutation.setSessionToken]: (state, data) => {
        state.oktaSession = data;
    },
    [storeMutation.setSessionExpirationTime]: async (state, postMutationData) => {
        const { dispatch } = postMutationData;
        state.authClient.session.get().then((session) => {
            const expirationDate = addDays(new Date(session.createdAt), 90);
            const expirationDateWithTimeout = new Date(expirationDate);
            if (new Date() > expirationDateWithTimeout) {
                dispatch(storeAction.submitLogout);
            } else if (session && session.status && session.status.toLowerCase() === 'active') {
                state.sessionExpirationTime = expirationDateWithTimeout - new Date();
                dispatch(storeAction.setSessionExpiration);
            }
        });
    },
    [storeMutation.updateSessionExpiration]: (state, reducedTime) => {
        if (state.sessionExpirationTime && reducedTime) {
            state.sessionExpirationTime -= reducedTime;
        }
    },
    [storeMutation.setPasswordRecoveryOptions]: (state, data) => {
        state.passwordRecoveryOptions = data;
    },
    [storeMutation.setSelectedRecoveryCodeType]: (state, data) => {
        state.selectedRecoveryCodeType = data;
    },
    [storeMutation.setPasswordResetToken]: (state, data) => {
        state.passwordResetToken = data.stateToken;
    },
    [storeMutation.setLoginErrorMessage]: (state, message) => {
        state.loginErrorMessage = message;
    },
    [storeMutation.setFromLogoutSession]: (state, data) => {
        state.fromLogoutSession = data || false;
    },
    [storeMutation.setUserRecoveryOptions]: (state, data) => {
        state.userRecoveryOptions = data;
    },
    [storeMutation.setPasswordFurtherAssistance]: (state, data) => {
        state.passwordFurtherAssistance = data;
    },
    [storeMutation.setSelectTypePasswordDelivery]: (state, data) => {
        state.selectTypePasswordDelivery = data;
    },
    [storeMutation.setStateToken]: (state, data) => {
        state.stateToken = data.stateToken;
    },
    [storeMutation.prepareAdditionalSecurityObjectForPassword]: (state, user) => {
        state.additionalSecurityObjectForPassword = {
            ...additionalSecurityForMember(),
            ...user,
        };
    },
    [storeMutation.setPermissionsData]: (state, data) => {
        const { permissions, communities } = data;
        state.permissionsData = permissions;
        state.userCommunities = communities;
        state.isMemberProspect = permissions.isProspect;
        state.isPendingResignation = permissions.isPendingResignation;
        state.isChapterManager = communities.some(com => com.roles && com.roles.includes(roles.CHAPTER_MANAGER));
        state.hasConnectAccess = permissions.hasConnectAccess !== false;
        state.isSpeakerView = permissions.isNonMemberSpeaker;
        state.memberAdminControls = {
            ...state.memberAdminControls,
            canImpersonate: permissions.canImpersonate,
            canAllowImpersonation: !!(permissions.userRoles && permissions.userRoles.some(item => item === roles.GROUP_SUPER_ADMIN)),
        };
    },
    [storeMutation.setIsExceptionOccurredOnGetPermissions]: (state, data) => {
        state.isExceptionOccurredOnGetPermissions = data;
    },
};

const authActions = {
    [storeAction.initiateLoginRedirect]: async ({ state }) => {
        sessionStorage.setItem(appConstant.storageKeys.loginRedirectModeTriggered, "true");
        await state.authClient.signInWithRedirect();
    },
    [storeAction.getFeatureFlag]: async ({ state }) => {
        return axios({
            url: `${state.baseURL}api/featureFlags`,
            //url: '/mocks/featureFlag.json',
            headers: {
                ...getBasicHeaders(),
                'Cache-Control': 'no-cache, no-store, max-age=0',
            },
            method: 'GET',
        })
            .then(async (response) => {
                return response.data;
            })
            .catch((err) => {
                throw err;
            });
    },
    [storeAction.sendMobileMessage]: async ({ rootGetters }, data) => {
        const isMobileApp = await rootGetters[storeGetters.isMobileAppBrowser];
        if (isMobileApp) {
            if (window && window.appInterface) {
                const androidInterface = window.appInterface;
                androidInterface.sendMobileMessage(data);
            } else if (window && window.webkit && window.webkit.messageHandlers
                && window.webkit.messageHandlers.sendMobileMessage) {
                const iosInterface = window.webkit.messageHandlers.sendMobileMessage;
                iosInterface.postMessage(data);
            }
        }
    },
    [storeAction.sendSessionExpirationNoticeToMobileApps]: async ({ rootGetters }) => {
        const isMobileApp = await rootGetters[storeGetters.isMobileAppBrowser];
        if (isMobileApp) {
            const fromUrl = document.location.href;
            if (window && window.appInterface) {
                const androidInterface = window.appInterface;
                androidInterface.sessionCookieExpired(fromUrl);
            } else if (window && window.webkit && window.webkit.messageHandlers
                && window.webkit.messageHandlers.sessionExpired) {
                const iosInterface = window.webkit.messageHandlers.sessionExpired;
                iosInterface.postMessage(fromUrl);
            }
        }
    },
    [storeAction.submitLogin]: ({ state, commit, dispatch }, authObject) => {
        return new Promise((resolve, reject) => {
            state.authClient
                .signIn({
                    username: authObject.username,
                    password: authObject.password,
                })
                .then((res) => {
                    if (res.status === 'SUCCESS') {
                        commit(storeMutation.setSessionToken, res.data);
                        dispatch(storeAction.authenticateUser, authObject.router)
                            .then(() => {
                                resolve();
                            })
                            .catch((err) => {
                                axios.defaults.headers.common.Authorization = '';
                                reject(err);
                            });
                    } else {
                        reject(res);
                    }
                })
                .catch((err) => {
                    commit(storeMutation.setLoginErrorMessage, erorrMessages.login.INVALID_USER_OR_PASSWORD);
                    reject(err);
                });
        });
    },
    [storeAction.setSessionInState]: ({ commit, dispatch }) => {
        commit(storeMutation.setSessionExpirationTime, { dispatch, router });
    },
    [storeAction.authenticateUser]: async ({ state, commit, dispatch }) => {
        axios.defaults.headers.common.Authorization = '';
        return new Promise((resolve, reject) => {
            state.authClient.token
                .getWithoutPrompt({
                    sessionToken: state.oktaSession.sessionToken,
                    responseType: 'token',
                    scopes: ['openid', 'email', 'profile'],
                    state: Math.random()
                        .toString(18)
                        .slice(2, 14)
                        .toUpperCase(),
                    nonce: Math.random()
                        .toString(18)
                        .slice(2, 14)
                        .toUpperCase(),
                })
                .then((res) => {
                    state.authClient.tokenManager.add('accessToken', res.tokens.accessToken);
                    const { accessToken } = res.tokens.accessToken;
                    const accessTokenData = accessToken && state.authClient.token.decode(accessToken);
                    dispatch(storeAction.setAuthDataInState, res.tokens.accessToken);
                    dispatch(storeAction.startOktaTokenRefreshTimer);
                    dispatch(storeAction.getPermissions)
                        .then(() => {
                            let urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
                            if (!urlParams.values().next()) {
                                urlParams = new URLSearchParams(window.location.search);
                            }

                            const redirectUrl = urlParams.get('fromURI');
                            if (redirectUrl) {
                                if (redirectUrl.indexOf("isLMS") >= 0) {
                                    store.dispatch(`auth/${storeAction.registerInAdobe}`).then((response) => {
                                        if (response && response.data) {
                                            window.open(redirectUrl, '_self');
                                        } else {
                                            store.commit(`ypoError/${storeMutation.setYpoErrorData}`, 1, { root: true });
                                            router.push({
                                                path: `/${routerNames.ERROR_PAGE}`,
                                            });
                                        }
                                    });
                                } else { window.open(redirectUrl, '_self'); }
                            } else {
                                if (accessTokenData && accessTokenData.payload.ypoId) {
                                    setUserId(accessTokenData.payload.ypoId);
                                    commit(storeMutation.setCurrentUserId, accessTokenData.payload.ypoId);
                                }
                                sessionStorage.setItem('loggedUserYpo', 'user authenticated!');
                                commit(storeMutation.setLoggedUser, 'user authenticated!');
                                dispatch(storeAction.setSessionInState, router);
                            }
                            resolve(res);
                        })
                        .catch((err) => {
                            reject(err);
                        });
                })
                .catch((err) => {
                    if (err && err.message === 'User is not assigned to the client application.') {
                        commit(storeMutation.setLoginErrorMessage, erorrMessages.login.NOT_ASSIGNED_OKTA_APP);
                    } else {
                        commit(storeMutation.setLoginErrorMessage, erorrMessages.login.INVALID_USER_OR_PASSWORD);
                    }
                    reject(err);
                });
        });
    },
    [storeAction.setAuthDataInState]: async ({ state, commit }, data) => {
        const accessTokenData = data.accessToken && state.authClient.token.decode(data.accessToken);
        const expirationDate = new Date(accessTokenData.payload.exp * 1000);
        axios.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
        const authData = {
            authProvider: 'okta',
            token: data.accessToken,
            tokenType: 'accessToken',
            expTime: expirationDate,
            userGuid: accessTokenData.payload.ypoId,
        };
        commit(storeMutation.setAuthData, authData);
    },
    [storeAction.fetchNewOktaToken]: async ({ state, dispatch }) => {
        return new Promise((resolve, reject) => {
            state.authClient.token
                .getWithoutPrompt({
                    responseType: 'token',
                    scopes: ['openid', 'email', 'profile'],
                    state: Math.random()
                        .toString(18)
                        .slice(2, 14)
                        .toUpperCase(),
                    nonce: Math.random()
                        .toString(18)
                        .slice(2, 14)
                        .toUpperCase(),
                })
                .then((res) => {
                    const { accessToken } = res.tokens;
                    state.authClient.tokenManager.add('accessToken', accessToken);
                    dispatch(storeAction.setAuthDataInState, accessToken);
                    dispatch(storeAction.startOktaTokenRefreshTimer);
                    resolve(res);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },
    [storeAction.refreshOktaAccessToken]: async ({ state, commit, dispatch }) => {
        if (!state.resettingAuthToken) {
            commit(storeMutation.resetAuthTokenStatus, true);
            setTimeout(() => {
                dispatch(storeAction.fetchNewOktaToken)
                    .then(() => {
                        // intentionally adding this
                        console.log("Token Refreshed!");
                        setTimeout(() => {
                            commit(storeMutation.resetAuthTokenStatus, false);
                        }, 60000);
                    })
                    .catch(() => {
                        commit(storeMutation.resetAuthTokenStatus, false);
                    });
            }, 2000); // delaying by 2 sec
        }
    },
    [storeAction.startOktaTokenRefreshTimer]: async ({ state, dispatch }) => {
        const timer = (state.authData.expTime - new Date()) - state.tokenRefreshTime;
        setTimeout(() => {
            dispatch(storeAction.refreshOktaAccessToken);
        }, timer);
    },
    [storeAction.registerInAdobe]: ({ state }) => {
        return axios({
            url: `${state.baseURL}profiles/api/register-member`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    },
    [storeAction.saveTermsAndConditions]: ({ state, rootState }, postBody) => {
        let url = `${state.baseURL}profiles/api/v2/persons/save-terms-and-conditions`;
        let method = 'PUT';
        let headers = getBasicHeaders();

        if (rootState.auth.isSpeakerView) {
            url = `${state.speakerBaseURL}speaker/api/speaker/save-terms-and-conditions`;
            method = 'POST';
            headers = getSpeakerBasicHeaders();
        }
        return axios({
            url,
            headers,
            method,
            data: postBody,
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                throw err;
            });
    },
    [storeAction.submitLogout]: async ({ state, dispatch }) => {
        await state.authClient.signOut({ clearTokensAfterRedirect: true });
        state.authClient.tokenManager.clear();
        dispatch(storeAction.handlePostLogoutCleanUp);
        setTimeout(() => {
            if (router && router.push) {
                router.push('/');
            }
        }, 4000);
    },
    [storeAction.logoutAndClearSession]: async ({ state, dispatch }) => {
        await state.authClient.revokeAccessToken();
        state.authClient.closeSession().finally(() => {
            state.authClient.tokenManager.clear();
            dispatch(storeAction.handlePostLogoutCleanUp);
        });
    },
    [storeAction.handlePostLogoutCleanUp]: ({ state, commit, dispatch }) => {
        commit(storeMutation.removeLoggedUser);
        commit(storeMutation.removeCurrentUserId);
        //resetting the app state
        commit(storeMutation.resetState);
        state.authClient.tokenManager.clear();
        dispatch(storeAction.clearYpoStoredDetails);
        //This is to clearTimeOuts which are not fired in the application
        let id = window.setTimeout(() => {}, 0);
        while (id--) {
            window.clearTimeout(id);
        }
        store.commit(storeMutation.setSessionExpiryNotice);
    },
    [storeAction.setSessionExpiration]: ({
                                             state,
                                             commit,
                                             dispatch,
                                             rootGetters,
                                         }) => {
        if (state.userAuthenticated) {
            const maxTimeForTimeout = 8.64e+7; // 24 hours
            const timeToTriggerWarning = 1.8e+6; // 30 minutes
            if (state.sessionExpirationTime > maxTimeForTimeout) {
                setTimeout(() => {
                    commit(storeMutation.updateSessionExpiration, maxTimeForTimeout);
                    dispatch(storeAction.setSessionExpiration, router);
                }, maxTimeForTimeout);
            } else {
                setTimeout(async () => {
                    const isMobileApp = await rootGetters[storeGetters.isMobileAppBrowser];
                    if (isMobileApp) {
                        if (window && window.appInterface) {
                            const androidInterface = window.appInterface;
                            androidInterface.sessionCookieAboutToExpire("");
                        } else if (window && window.webkit) {
                            const iosInterface = window.webkit.messageHandlers.sessionExpiryBefore;
                            iosInterface.postMessage("");
                        }
                    } else {
                        commit(storeMutation.setSessionExpiryNotice, true, { root: true });
                        commit(storeMutation.setFromLogoutSession, true);
                    }
                    dispatch(storeAction.logoutAfterSessionExpired);
                }, state.sessionExpirationTime - timeToTriggerWarning);
            }
        }
    },
    [storeAction.logoutAfterSessionExpired]: async ({ dispatch, rootGetters }) => {
        const isMobileApp = await rootGetters[storeGetters.isMobileAppBrowser];
        const maxTimeBeforeLogoutAfterNotification = 1.62e+6; // 27 minutes
        setTimeout(() => {
            if (!isMobileApp) {
                dispatch(storeAction.submitLogout);
            }
        }, maxTimeBeforeLogoutAfterNotification);
    },
    [storeAction.submitUsernameRecoveryEmail]: ({ state, commit }, email) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/${email}/recovery/personname`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                console.log('username', err);
                commit(storeMutation.setLoginErrorMessage, erorrMessages.login.EMAIL_NOT_FOUND);
                throw err;
            });
    },
    [storeAction.submitUsernameFurtherAssistance]: ({ state, commit }, user) => {
        return axios({
            //mock for local testing
            //url: '/mocks/recoverUsernameResponse.json',
            url: `${state.baseURL}identities/api/identities/recovery/username/assistance/${user.lastName}/${user.birthDate}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setUserRecoveryOptions, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                commit(storeMutation.setLoginErrorMessage, erorrMessages.login.DETAILS_DONT_MATCH_ERROR);
                console.log(err);
                throw err;
            });
    },
    [storeAction.submitUsernameRecoveryOption]: ({ state }, option) => {
        return axios({
            //mock for local testing
            //url: '/mocks/recoverUsernameResponse.json',
            url: `${state.baseURL}identities/api/identities/${state.userRecoveryOptions.identityId}/recovery/username/${option}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                // commit(storeMutation.setUserRecoveryOptions, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.submitPasswordFurtherAssistance]: ({ state, commit }, user) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/recovery/password/assistance/${user.lastName}/${user.birthDate}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setPasswordFurtherAssistance, response.data);
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setLoginErrorMessage, err.response.data.message);
                throw err;
            });
    },
    [storeAction.submitPasswordFurtherSecurityForMember]: ({ state, commit }, user) => {
        const phone = removeSpaces(user.countryCode + user.phoneNumber);
        return axios({
            url: `${state.baseURL}identities/api/identities/recovery/password/assistance/${user.lastName}/${user.birthDate}/${phone}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setPasswordFurtherAssistance, response.data);
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setLoginErrorMessage, loginConstants.errors.INVALID_PLEASE_RETRY);
                throw err;
            });
    },
    [storeAction.submitLoginAssistanceForSpousePartner]: ({ state, commit }, user) => {
        const phone = removeSpaces(user.countryCode + user.phoneNumber);
        return axios({
            url: `${state.baseURL}identities/api/identities/${user.urlRecoveryType}/${user.recoveryType}/assistance/${user.firstName}/${user.lastName}/${phone}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                if (user.recoveryType === loginConstants.spousePartnerLoginAssistance.PASSWORD) {
                    commit(storeMutation.setPasswordFurtherAssistance, response.data);
                } else {
                    commit(storeMutation.setUserRecoveryOptions, response.data);
                }
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setLoginErrorMessage, loginConstants.errors.INVALID_PLEASE_RETRY);
                throw err;
            });
    },
    [storeAction.submitSelectTypePasswordDelivery]: ({ state, commit }, option) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/${state.passwordFurtherAssistance.identityId}/recovery/password/${option.recoveryCodeType}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                response.data.recoveryCodeType = option.recoveryCodeType;
                response.data.destination = option.destination;
                commit(storeMutation.setSelectTypePasswordDelivery, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                commit(storeMutation.setLoginErrorMessage, erorrMessages.login.PASSWORD_RESET_GENERIC_ERROR);
                throw err;
            });
    },
    [storeAction.getPasswordRecoveryOptions]: ({ state, commit }, email) => {
        return axios({
            //mock for local testing
            //url: '/mocks/recoverUsernameResponse.json',
            url: `${state.baseURL}identities/api/identities/${email}/recovery/password`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setPasswordFurtherAssistance, response.data);
                commit(storeMutation.setPasswordRecoveryOptions, response.data);
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setLoginErrorMessage, erorrMessages.login.PASSWORD_RESET_GENERIC_ERROR);
                throw err;
            });
    },
    [storeAction.submitPasswordRecoveryOption]: ({ state, commit }, option) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/${state.passwordRecoveryOptions.IdentityId}/recovery/password/${option}`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setSelectedRecoveryCodeType, option);
                commit(storeMutation.setPasswordResetToken, response.data);
                return response;
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
                throw err;
            });
    },
    [storeAction.submitRecoveryToken]: ({ commit, state }, token) => {
        const requestBody = {
            recoveryToken: token,
        };

        return axios({
            url: `${state.oktaRootUrl}api/v1/authn/recovery/token`,
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                //TODO: The state token mapping might not be right.  Couldn't get the endpoint working before leaving.  Sorry!
                commit(storeMutation.setStateToken, response.data);
            })
            .catch((err) => {
                //TODO: handle error
                commit(storeMutation.setLoginErrorMessage, erorrMessages.login.INVALID_IDENTIFICATION_CODE);
                console.log(err);
                throw err;
            });
    },
    [storeAction.submitRecoverySmsCode]: ({ commit, state }, code) => {
        const requestBody = {
            passCode: code,
            stateToken: state.selectTypePasswordDelivery.stateToken,
        };

        return axios({
            url: `${state.oktaRootUrl}api/v1/authn/recovery/factors/SMS/verify`,
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                console.log(response.data);
                commit(storeMutation.setStateToken, response.data);
            })
            .catch((err) => {
                //TODO: handle error
                commit(storeMutation.setLoginErrorMessage, erorrMessages.login.INVALID_IDENTIFICATION_CODE);
                console.log(err);
                throw err;
            });
    },
    [storeAction.answerSecurityQuestion]: ({ commit, state }) => {
        const requestBody = {
            answer: 'ypodefaultanswer',
            stateToken: state.stateToken,
        };

        return axios({
            url: `${state.oktaRootUrl}api/v1/authn/recovery/answer`,
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                console.log(response.data);
                commit(storeMutation.setStateToken, response.data);
            })
            .catch((err) => {
                //TODO: handle error
                console.log(err);
            });
    },
    [storeAction.resetPassword]: ({ commit, state }, postBody) => {
        const requestBody = {
            stateToken: state.stateToken,
            newPassword: postBody.newPass,
        };

        return axios({
            url: `${state.oktaRootUrl}api/v1/authn/credentials/reset_password`,
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                console.log(response.data);
            })
            .catch((err) => {
                //TODO: handle error
                if (
                    err.response.data.errorCauses[0].errorSummary ===
                    erorrMessages.login.PASSWORD_CANNOT_BE_YOUR_CURRENT_PASSWORD
                ) {
                    commit(
                        storeMutation.setLoginErrorMessage,
                        erorrMessages.login.NEW_PASSOWRD_CANNOT_BE_THE_SAME_AS_YOUR_CURRENT_PASSWORD,
                    );
                } else {
                    commit(storeMutation.setLoginErrorMessage, err.response.data.errorSummary);
                }
                console.log(err);
                throw err;
            });
    },
    [storeAction.handleLoginErrorMessage]: ({ commit }, message) => {
        commit(storeMutation.setLoginErrorMessage, message);
    },
    [storeAction.clearYpoStoredDetails]: ({ commit }) => {
        delete axios.defaults.headers.common['X-Connecting-As'];
        delete axios.defaults.headers.common.Authorization;
        sessionStorage.removeItem('loggedUserYpo');
        commit(`eventDirectory/${storeMutation.setEventDirectorySort}`, null, { root: true });
        commit(`communityDirectory/${storeMutation.setCommunityDirectorySort}`, null, { root: true });
        commit(`memberDirectory/${storeMutation.setMemberDirectorySort}`, null, { root: true });
        sessionStorage.removeItem(appConstant.storageKeys.impersonationKey);
        sessionStorage.removeItem('memberDirectorySort');
        sessionStorage.removeItem('eventDirectorySort');
        sessionStorage.removeItem('communityDirectorySort');
        sessionStorage.removeItem('contentDirectorySort');
        sessionStorage.removeItem(SpeakerApplicants.PREVIOUS_PAGE);
        sessionStorage.removeItem(appConstant.storageKeys.memberDirectoryStoredFilters);
        sessionStorage.removeItem(appConstant.storageKeys.eventDirectoryStoredFilters);
        sessionStorage.removeItem(appConstant.storageKeys.communityDirectoryStoredFilters);
        sessionStorage.removeItem(appConstant.storageKeys.contentDirectoryStoredFilters);
        sessionStorage.removeItem(appConstant.storageKeys.ypoSfLoginToken);
        sessionStorage.removeItem(appConstant.storageKeys.ypoAttributionData);
    },
    [storeAction.getPermissions]: async ({ commit, state, dispatch }) => {
        return new Promise((resolve, reject) => {
            axios({
                url: `${state.baseURL}communities/api/community/GetUserPermissionsAndCommunities`,
                headers: getBasicHeaders(),
                method: 'Get',
            })
                .then(async (response) => {
                    if (response.data) {
                        commit(storeMutation.setPermissionsData, response.data);
                        commit(`chapter/${storeMutation.setusersCommunitiesList}`, response.data.communities, {
                            root: true,
                        });
                        commit(`chapterStudio/${storeMutation.setChapterStudioChapterList}`, response.data.communities, {
                            root: true,
                        });
                        if (
                            !response.data.permissions.isNonMemberSpeaker &&
                            response.data.permissions.hasConnectAccess === false
                        ) {
                            commit(storeMutation.setLoginErrorMessage, erorrMessages.login.INVALID_USER_OR_PASSWORD);
                            // commit(storeMutation.setLoginErrorMessage, erorrMessages.login.INVALID_USER_OR_PASSWORD);
                        }
                        resolve(response.data);
                    } else {
                        throw new Error();
                    }
                })
                .catch((err) => {
                    console.log(err);
                    dispatch(storeAction.submitLogout)
                        .then(() => {
                            commit(
                                storeMutation.setLoginErrorMessage,
                                erorrMessages.login.UNABLE_TO_CONNECT_TO_THE_SERVER,
                            );
                            reject(new Error(erorrMessages.login.UNABLE_TO_CONNECT_TO_THE_SERVER));
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
        });
    },
};

const authGetters = {
    [storeGetters.passwordRecoveryOptions]: state => state.passwordRecoveryOptions,
    [storeGetters.authData]: state => state.authData,
    [storeGetters.authFeatureFlag]: state => state.authFeatureFlag,
    [storeGetters.getSelectedCodeRecoveryType]: state => state.selectedRecoveryCodeType,
    [storeGetters.isAuthenticated]: state => !!state.userAuthenticated,
    [storeGetters.authenticatedUserId]: state =>
        state.authClient.tokenManager.get('accessToken').then((token) => {
            return token ? state.authClient.token.decode(token.accessToken).sub : null;
        }),
    [storeGetters.authenticatedUserEmail]: state =>
        state.authClient.tokenManager.get('accessToken').then((token) => {
            return token ? state.authClient.token.decode(token.accessToken).email : null;
        }),
    [storeGetters.getLoginError]: state => state.loginErrorMessage,
    [storeGetters.userRecoveryOptions]: state => state.userRecoveryOptions,
    [storeGetters.passwordFurtherAssistance]: state => state.passwordFurtherAssistance,
    [storeGetters.getSelectTypePasswordDelivery]: state => state.selectTypePasswordDelivery,
    [storeGetters.getMemberAdditionalSecurityObject]: state => state.additionalSecurityObjectForPassword,
    [storeGetters.getPostLoginRedirectRoute]: state => state.postLoginRedirectRoute,
    [storeGetters.isMemberProspect]: state => state.isMemberProspect,
    [storeGetters.canViewChapterStudioPage]: state => state.permissionsData && state.permissionsData.canViewChapterStudioPage,
    [storeGetters.canViewManageChapterStudioPage]: state => state.permissionsData && state.permissionsData.canManageChapterStudio,
    [storeGetters.isChapterManager]: state => state.isChapterManager,
    [storeGetters.shouldDefaultToChapterStudioPage]: (state, getters) => {
        const isChapterManager = getters[storeGetters.isChapterManager];
        const canViewChapterStudioPage = getters[storeGetters.canViewChapterStudioPage];
        return canViewChapterStudioPage && isChapterManager;
    },
    [storeGetters.isChaptersAndRegionsTeamMember]: state => state.permissionsData && state.permissionsData.isChaptersAndRegionsTeamMember,
    [storeGetters.isYngMember]: state => state.permissionsData && state.permissionsData.isYngMember,
    [storeGetters.permissionsData]: state => state.permissionsData,
    [storeGetters.userCommunities]: state => state.userCommunities,
    [storeGetters.isTeamProductManager]: state => !!(
        state.permissionsData
        && state.permissionsData.userRoles
        && state.permissionsData.userRoles
            .some(r => r === roles.TEAM_MEMBER_PRODUCT_MANAGEMENT)
    ),
    [storeGetters.isGroupSuperAdmin]: state => !!(
        state.permissionsData
        && state.permissionsData.userRoles
        && state.permissionsData.userRoles
            .some(r => r === roles.GROUP_SUPER_ADMIN)),
    [storeGetters.isPendingResignation]: state => state.isPendingResignation,
    [storeGetters.isExceptionOccurredOnGetPermissions]: state => state.isExceptionOccurredOnGetPermissions,
    [storeGetters.hasConnectAccess]: state => state.hasConnectAccess,
    [storeGetters.currentUserId]: state => state.currentUserId,
    [storeGetters.isExternalSpeaker]: state => state.isExternalSpeaker,
    [storeGetters.isSpeakerView]: state => state.isSpeakerView,
    [storeGetters.getTokenExpirationTime]: state => state.sessionExpirationTime,
    [storeGetters.cookiesPolicyAccepted]: state => state.cookiesPolicyAccepted,
    [storeGetters.isImpersonationMode]: state => state.isImpersonationMode,
    [storeGetters.memberAdminControls]: state => state.memberAdminControls,
    [storeGetters.tokenRefreshTime]: state => state.tokenRefreshTime,
};

export default {
    namespaced: true,
    state: {
        initialState: authState(),
        ...authState(),
    },
    mutations: { ...authMutations },
    actions: { ...authActions },
    getters: { ...authGetters },
};
